<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
            ajax-path="aws/lightsail/instances"
            @ajaxResponse="handleAjaxResponse"
        />
        <app-modal
            :show.sync="showCreateModal"
            width="md:w-1/2 lg:w-2/3"
        >
            <template slot="header">Create an Instance</template>
            <template slot="body">
                <app-input
                    v-model="createForm.instanceNames"
                    placeholder="Instance name"
                    :error="createForm.errors.get('instanceNames')"
                    @updated="createForm.errors.clear('instanceNames')"
                    required
                    focus
                    :alpha-numeric="true"
                />
                <app-select
                    v-model="createForm.region"
                    placeholder="Select your instance location"
                    :error="createForm.errors.get('region')"
                    @updated="createForm.errors.clear('region')"
                    :options="regions"
                    label-key="displayName"
                    value-key="name"
                    required
                />
                <template v-if="availabilityZones && availabilityZones.length > 0">
                    <h2 class="mt-5">Select an Availability Zone</h2>
                    <div class="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-5 mt-3">
                        <div 
                            v-for="(zone, index) in availabilityZones"
                            :key="index"
                            class="flex items-center border p-3 rounded cursor-pointer"
                            :class="createForm.availabilityZone == zone.zoneName ? 'border-blue-500 bg-blue-100 font-bold' : 'border-gray-300'"
                            @click="() => {
                                createForm.availabilityZone = zone.zoneName;
                            }"
                        >
                            <div class="bg-blue-500 rounded text-white px-3 py-2">{{ zone.zoneName.substr(zone.zoneName.length - 1).toUpperCase() }}</div>
                            <div class="ml-3">
                                {{ zone.zoneName }}
                            </div>
                        </div>
                    </div>
                    <h2 class="mt-5">Select a platform</h2>
                    <div class="grid grid-cols-2 gap-2 mb-5 mt-3">
                        <div 
                            v-for="(platform, index) in regionPlatforms"
                            :key="index"
                            class="flex items-center border p-3 rounded cursor-pointer"
                            :class="selectedPlatform.key == platform.key ? 'border-blue-500 bg-blue-100 font-bold' : 'border-gray-300'"
                            @click="() => {
                                selectedPlatform = platform;
                                selectedBlueprintType = platform.types[0];
                                createForm.blueprintId = platform.types[0]['items'][0].blueprintId;
                            }"
                        >
                            <img :src="platform.icon" width="48" height="48"/>
                            <div class="ml-3">
                                {{ platform.label }} <br>
                                <span class="text-gray-500 font-normal">{{ platform.total_blueprints }} blueprints</span>
                            </div>
                        </div>
                    </div>
                    <h2>Select a blueprint</h2>
                    <div class="grid grid-cols-2 gap-2 mb-5 mt-3">
                        <div 
                            v-for="(type, index) in selectedPlatform.types"
                            :key="index"
                            class="border p-3 rounded cursor-pointer text-center"
                            :class="selectedBlueprintType.key == type.key ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300'"
                            @click="() => {
                                selectedBlueprintType = type;
                                createForm.blueprintId = type.items[0].blueprintId;
                            }"
                        >
                            {{ type.label }}
                        </div>
                    </div>
                    <div class="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-5 mt-3">
                        <div 
                            v-for="item in selectedBlueprintType.items"
                            :key="item.id"
                            class="flex items-center border p-3 rounded cursor-pointer"
                            :class="createForm.blueprintId == item.blueprintId ? 'border-blue-500 bg-blue-100 font-bold' : 'border-gray-300'"
                            @click="createForm.blueprintId = item.blueprintId"
                        >
                            <img :src="item.icon" width="48" height="48"/>
                            <div class="ml-3">
                                {{ item.name }} <br>
                                <span class="text-gray-500 truncate w-20 inline-block font-normal">{{ item.version }}</span>
                            </div>
                        </div>
                    </div>
                    <h2>Choose your instance plan</h2>
                    <div class="grid grid-cols-3 lg:grid-cols-5 gap-2 mb-5 mt-3">
                        <div 
                            v-for="bundle in regionBundles"
                            :key="bundle.id"
                            class="border p-3 rounded cursor-pointer text-center"
                            :class="createForm.bundleId == bundle.bundleId ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300'"
                            @click="() => {
                                createForm.bundleId = bundle.bundleId;
                            }"
                        >
                            <h2 class="font-bold text-lg"><span class="font-thin text-xs">$</span> {{ bundle.price }} <sub class="text-xs font-thin">/ month</sub></h2>
                            <div class="text-sm">
                                <div>{{ bundle.ramSizeInGb == 0.5 ? '512 MB' : `${bundle.ramSizeInGb} GB` }}</div>
                                <div>{{ bundle.cpuCount }} {{ bundle.cpuCount <= 1 ? 'vCPU' : 'vCPUs' }}</div>
                                <div>{{ bundle.diskSizeInGb }} GB SSD</div>
                                <div>{{ Math.floor(bundle.transferPerMonthInGb / 1000) }} TB</div>
                            </div>
                        </div>
                    </div>
                    <h2 class="mb-3">Tags</h2>
                    <div
                            v-for="(tag, index) in createForm.tags"
                            :key="index"
                            class="mb-3"
                        >               
                        <div class="flex items-center">
                            <input                        
                                v-model="createForm.tags[index].key"
                                class="border border-gray-400 px-3 py-2 w-full rounded"
                                placeholder="Key" 
                            />
                            <input                        
                                v-model="createForm.tags[index].value"
                                class="border border-gray-400 px-3 py-2 w-full rounded ml-2"
                                placeholder="Value" 
                            />
                            <button 
                                class="p-2 rounded hover:bg-red-600 bg-red-500 text-white ml-2 shadow"
                                v-if="createForm.tags.length > 1"
                                @click="deleteTag(createForm, index)"
                            ><delete-icon width="20" height="20"/></button>
                            <button 
                                v-if="index == (createForm.tags.length - 1)"
                                class="p-2 rounded hover:bg-green-600 bg-green-500 text-white ml-2 shadow"
                                @click="addTag(createForm)"
                            ><add-icon width="20" height="20"/></button>
                        </div>
                        <div 
                            class="text-red-500 mt-1" 
                            v-if="createForm.errors.has(`tag.${index}`)"
                        >{{ createForm.errors.get(`tag.${index}`) }}</div>
                    </div>
                </template>                        
                <div class="text-right mt-8">
                    <app-button
                        class="btn-white"
                        @click="showCreateModal = false"
                        :disable="creatingInstance"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="createInstance"
                        :loading="creatingInstance"
                    >Create</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showSSHKeysModal"
            width="md:w-4/5"
        >
            <template slot="header">{{ selected.name }} SSH Keys</template>
            <template slot="body">
                <tabulator
                    :columns="sshKeysColumns"
                    :actions="sshKeysActions"
                    :refresh.sync="refreshSSHKeysTable"
                    :loading="sshKeysTableIsLoading"
                    :ajax-path="`aws/lightsail/instances/${selected.id}/ssh`"
                />
            </template>
        </app-modal>
        <app-modal
            :show.sync="showCreateSSHKeyModal"
            width="md:w-1/3"
        >
            <template slot="header">Create SSH Key for {{ selected.name }}</template>
            <template slot="body">
                <app-input
                    v-model="createSSHKeyForm.keyPairName"
                    placeholder="Key Pair Name"
                    :error="createSSHKeyForm.errors.get('keyPairName')"
                    @updated="createSSHKeyForm.errors.clear('keyPairName')"
                    required
                    focus
                    :alpha-numeric="true"
                />
                <div class="text-right mt-8">
                    <app-button
                        class="btn-white"
                        @click="showCreateSSHKeyModal = false"
                        :disable="creatingSSHKey"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="createSSHKey"
                        :loading="creatingSSHKey"
                    >Create</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showDownloadModal"
            :hasHeader="false"
            width="md:w-1/4"
        >
            <template slot="body">
                <div class="flex items-center justify-center">
                    <app-spinner class="text-gray-600"/> <span class="ml-2">Downloading...</span>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    import Http from '@/helpers/Http';
    
    export default {
        data() {
            return {
                showSSHKeysModal: false,
                showCreateModal: false,
                showRecordsModal: false,
                showCreateRecordModal: false,
                showEditRecordModal: false,
                showCreateSSHKeyModal: false,
                showDownloadModal: false,              
                creatingInstance: false,
                creatingRecord: false,
                creatingSSHKey: false,
                updatingRecord: false,
                recordsAjaxParams: {},
                refreshTable: false,
                refreshRecordsTable: false,
                refreshSSHKeysTable: false,
                sshKeysTableIsLoading: false,
                tableIsLoading: false,
                tableLoadingText: null,
                selected: {},
                platforms: [],
                bundles: [],
                regions: [],
                regionPlatforms: [],
                regionBundles: [],
                availabilityZones: [],
                selectedPlatform: {},
                selectedBlueprintType: {},
                createForm: new Form({
                    blueprintId: null,
                    bundleId: null,
                    availabilityZone: null,
                    instanceNames: null,
                    region: null,
                    tags: [{key: '', value: ''}],
                }),
                createSSHKeyForm: new Form({
                    keyPairName: '',
                }),
                sshKeysColumns: [
                    {
                        title: 'Name',
                        field: 'name',
                        headerSort: false,
                        minWidth: 150,
                    },
                    {
                        title: 'Public Key',
                        field: 'publicKeyBase64',
                        headerSort: false,
                        minWidth: 100,
                        formatter({_cell}) {
                            if (_cell.row.data.publicKeyBase64) {
                                return '*******';
                            }

                            return '--';
                        }
                    },
                    {
                        title: 'Private Key',
                        field: 'privateKeyBase64',
                        headerSort: false,
                        minWidth: 100,
                        formatter({_cell}) {
                            if (_cell.row.data.privateKeyBase64) {
                                return '*******';
                            }

                            return '--';
                        }
                    },
                    {
                        title: 'Fingerprint',
                        field: 'fingerprint',
                        headerSort: false,
                        minWidth: 100,
                        formatter({_cell}) {
                            if (_cell.row.data.fingerprint) {
                                return '*******';
                            }

                            return '--';
                        }
                    },
                    {
                        title: 'Support Code',
                        field: 'supportCode',
                        headerSort: false,
                        minWidth: 200,
                        formatter({_cell}) {
                            if (_cell.row.data.supportCode) {
                                return _cell.row.data.supportCode;
                            }

                            return '--';
                        }
                    }
                ],
                sshKeysActions: [
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showCreateSSHKeyModal = true;
                            this.createSSHKeyForm.reset();
                            this.createSSHKeyForm.errors.clear();
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: keys => {
                            this.deleteSSHKey(keys[0]);
                        },
                        isEligible(keys) {
                            return keys[0].arn;
                        }
                    },
                ],                      
                columns: [
                    {
                        title: 'Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 250,
                        formatter: ({_cell}) => {
                            const data = _cell.row.data;
                            const region = this.regions.find(region => region.name == data.location.regionName);
                            const totalTags = data.tags.length;
                            let tagsHTML = '';


                            for (let i = 0; i < totalTags; i++) {
                                tagsHTML += `<div class="text-xs" style="color:#718096;">${data.tags[i].key}`;
                                
                                if (data.tags[i].value) {
                                    tagsHTML += ` -> ${data.tags[i].value}`;
                                }
                                
                                tagsHTML += `</div>`;
                            }

                            return `
                                <div style="white-space:normal;">
                                    <div>${data.name}</div>
                                    <div class="text-xs" style="color:#718096;">${data.hardware.ramSizeInGb} GB RAM, ${data.hardware.cpuCount} vCPU, ${data.hardware.disks[0].sizeInGb} GB SSD</div>
                                    <div class="text-xs" style="color:#718096;">${data.blueprintName}</div>
                                    <div class="text-xs" style="color:#718096;">${region.displayName}, Zone ${data.location.availabilityZone.replace(region.name, '').toUpperCase()} (${data.location.availabilityZone})</div>
                                    ${tagsHTML}
                                </div>
                            `;
                        }
                    },
                    {
                        title: 'Region', 
                        field: 'location.regionName', 
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Public IP',
                        field: 'publicIpAddress',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Private IP',
                        field: 'privateIpAddress',
                        minWidth: 150,
                    },
                    {
                        title: 'Status',
                        field: 'state.name',
                        headerSort: false,
                        minWidth: 100,
                    }
                ],
                actions: [
                    {
                        label: 'SSH Keys',
                        icon: () => import('@/icons/VpnKeyOutlined.vue'),
                        handler: instances => this.getSSHKeys(instances[0]),
                    },
                    {
                        label: 'Stop',
                        icon: () => import('@/icons/StopOutlined.vue'),
                        handler: instances => {
                            this.stopInstance(instances[0]);
                        },
                        isEligible(instances) {
                            return instances[0].state.code == 16; //running
                        }
                    },
                    {
                        label: 'Start',
                        icon: () => import('@/icons/PlayArrowOutlined.vue'),
                        handler: instances => {
                            this.startInstance(instances[0]);
                        },
                        isEligible(instances) {
                            return instances[0].state.code != 16; //not running
                        }
                    },
                    {
                        label: 'Reboot',
                        icon: () => import('@/icons/PowerSettingsOutlined.vue'),
                        handler: instances => {
                            this.rebootInstance(instances[0]);
                        },
                        isEligible(instances) {
                            return instances[0].state.code == 16; //running
                        }
                    },
                    {
                        label: 'Download PEM file',
                        icon: () => import('@/icons/CloudDownloadOutlined.vue'),
                        handler: instances => {
                            this.downloadPEMFile(instances[0]);
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: selected => {
                            this.deleteInstance(selected[0]);
                        }
                    },                                       
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync,
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showCreateModal = true;
                            this.createForm.reset();
                            this.createForm.errors.clear();
                            this.addTag(this.createForm);
                        }
                    },
                ],
            }
        },
        watch: {
            'createForm.region'(newValue) {
                this.availabilityZones = [];
                this.regionPlatforms = [];
                this.regionBundles = [];
                this.createForm.availabilityZone = null;

                if (newValue) {
                    const region = this.regions.find(region => region.name == newValue);
                    this.availabilityZones = region.availabilityZones;
                    this.createForm.availabilityZone = region.availabilityZones[0].zoneName;
                    this.regionPlatforms = this.platforms.find(platform => platform.region_id == region.id).items;
                    this.regionBundles = this.bundles.find(bundle => bundle.region_id == region.id).items;
                    
                    this.selectedPlatform = this.regionPlatforms[0];
                    this.selectedBlueprintType = this.regionPlatforms[0]['types'][0];
                    this.createForm.blueprintId = this.regionPlatforms[0]['types'][0]['items'][0].blueprintId;
                    this.createForm.bundleId = this.regionBundles[0].bundleId;
                }
            }
        },
        methods: {            
            createInstance() {
                this.creatingInstance = true;
                this.createForm.errors.clear();
                Http.post('aws/lightsail/instances', this.createForm.payload())
                    .then(() => {
                        this.creatingInstance = false;
                        this.showCreateModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Instance was successfully created.'
                        });
                    })
                    .catch(({ response }) => {
                        this.creatingInstance = false;
                        this.createForm.errors.record(response.data);
                    })
            },
            deleteInstance(selected) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${selected.name}</strong>" instance permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.delete(`aws/lightsail/instances/${selected.id}`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Instance was successfully deleted.'
                        })
                    }
                });
            },
            startInstance(selected) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to turn on "<strong>${selected.name}</strong>" instance.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, turn it on!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.patch(`aws/lightsail/instances/${selected.id}/start`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Instance was successfully turned on.'
                        })
                    }
                });
            },
            stopInstance(selected) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to turn off "<strong>${selected.name}</strong>" instance.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, turn it off!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.patch(`aws/lightsail/instances/${selected.id}/stop`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Instance was successfully turned off.'
                        })
                    }
                });
            },
            rebootInstance(selected) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to reboot "<strong>${selected.name}</strong>" instance.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, reboot it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.patch(`aws/lightsail/instances/${selected.id}/reboot`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Instance was successfully rebooted.'
                        })
                    }
                });
            },
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                Http.patch('aws/lightsail/instances/sync')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = true;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.',
                        })
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    })
            },
            handleAjaxResponse(response) {
                this.platforms = response.platforms;
                this.regions = response.regions;
                this.bundles = response.bundles;
            },
            addTag(form) {
                form.tags.push({key: '', value: ''});
            },
            deleteTag(form, index) {
                form.tags.splice(index, 1);
            },
            getSSHKeys(instance) {
                this.selected = instance;
                this.showSSHKeysModal = true;                
            },
            createSSHKey() {
                this.creatingSSHKey = true;
                this.createSSHKeyForm.errors.clear();
                Http.post(`aws/lightsail/instances/${this.selected.id}/ssh`, this.createSSHKeyForm.payload())
                    .then(({ data }) => {
                        this.creatingSSHKey = false;
                        this.showCreateSSHKeyModal = false;
                        this.refreshSSHKeysTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'SSH key was successfully created.'
                        })
                    })
                    .catch(({ response }) => {
                        this.creatingSSHKey = false;
                        this.createSSHKeyForm.errors.record(response.data);
                    })
            },
            deleteSSHKey(key) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${key.name}</strong>" ssh key.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.delete(`aws/lightsail/instances/${this.selected.id}/ssh/${key.name}`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshSSHKeysTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'SSH key was successfully deleted.'
                        })
                    }
                });
            },
            downloadPEMFile(instance) {
                this.showDownloadModal = true;
                Http.get(`aws/lightsail/instances/${instance.id}/ssh/download`, {responseType: 'blob'})
                    .then((response) => {
                        this.showDownloadModal = false;
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `LightsailDefault-${instance.location.regionName}.pem`);
                        document.body.appendChild(link);
                        link.click();
                    })
                    .catch(({ response }) => {
                        this.showDownloadModal = false;
                    });
            }       
        }
    }
</script>